/* Lo Esencial — native wishlist (Lista de deseos)
 *
 * Replaces the bestpush "Notify Me" app's wishlist half. The app's back-in-stock
 * half is already redundant: the theme ships its own waitlist card on the PDP.
 *
 * LAYER 1 (this file): per-device persistence via localStorage.
 * LAYER 2 (not built): cross-device sync needs an App Proxy backed by a
 * function holding the Admin token. The Admin token must never reach the
 * theme, so there is no storefront-only way to write a customer metafield.
 * Everything here is written so that layer can be added underneath without
 * touching the UI: all reads/writes funnel through Store.get/Store.set.
 *
 * Item shape (kept deliberately small — it is mirrored into a metafield later):
 *   { id: <variantId>, h: <productHandle>, t: <title>, v: <variantTitle>,
 *     p: <priceCents>, img: <src>, url: <url>, ts: <addedAt> }
 */
(function () {
  'use strict';

  var KEY = 'le:wishlist:v1';
  var EVT = 'le:wishlist:updated';
  var MAX = 100; // hard ceiling so a wedged loop cannot balloon localStorage

  // ---- store ------------------------------------------------------------
  var Store = {
    get: function () {
      try {
        var raw = window.localStorage.getItem(KEY);
        if (!raw) return [];
        var list = JSON.parse(raw);
        return Array.isArray(list) ? list : [];
      } catch (e) {
        // Private mode / disabled storage / corrupt JSON: behave as empty
        // rather than throwing and taking the whole PDP's JS down with it.
        return [];
      }
    },
    set: function (list) {
      try {
        window.localStorage.setItem(KEY, JSON.stringify(list.slice(0, MAX)));
      } catch (e) { /* quota or private mode — state stays in memory only */ }
      document.dispatchEvent(new CustomEvent(EVT, { detail: { count: list.length } }));
    }
  };

  function has(variantId) {
    var id = String(variantId);
    return Store.get().some(function (it) { return String(it.id) === id; });
  }

  function add(item) {
    var list = Store.get();
    if (list.some(function (it) { return String(it.id) === String(item.id); })) return list;
    list.unshift(item);
    Store.set(list);
    return list;
  }

  function remove(variantId) {
    var id = String(variantId);
    var list = Store.get().filter(function (it) { return String(it.id) !== id; });
    Store.set(list);
    return list;
  }

  function toggle(item) {
    return has(item.id) ? { list: remove(item.id), added: false }
                        : { list: add(item), added: true };
  }

  // ---- hearts -----------------------------------------------------------
  function paintButton(btn, on) {
    btn.classList.toggle('is-active', on);
    btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    btn.setAttribute(
      'aria-label',
      on ? 'Quitar de tu lista de deseos' : 'Guardar en tu lista de deseos'
    );
  }

  function itemFrom(btn) {
    var d = btn.dataset;
    return {
      id: d.wishlistVariant,
      h: d.wishlistHandle || '',
      t: d.wishlistTitle || '',
      v: d.wishlistVariantTitle || '',
      p: parseInt(d.wishlistPrice, 10) || 0,
      img: d.wishlistImage || '',
      url: d.wishlistUrl || '',
      ts: Date.now()
    };
  }

  function syncAllButtons() {
    document.querySelectorAll('[data-wishlist-toggle]').forEach(function (btn) {
      paintButton(btn, has(btn.dataset.wishlistVariant));
    });
    document.querySelectorAll('[data-wishlist-count]').forEach(function (el) {
      var n = Store.get().length;
      el.textContent = n;
      el.hidden = n === 0;
    });
  }

  // Delegated so hearts inside re-rendered sections (PLP filters, drawer,
  // wishlist page) keep working without re-binding.
  //
  // CAPTURE phase, deliberately. Product-card hearts sit inside the card's
  // <a href="/products/..."> and something upstream calls stopPropagation() on
  // the bubble path to stop the card navigating — so a bubble-phase document
  // listener never sees those clicks at all. Capture runs before any of that.
  document.addEventListener('click', function (ev) {
    var btn = ev.target.closest && ev.target.closest('[data-wishlist-toggle]');
    if (!btn) return;
    ev.preventDefault();
    ev.stopPropagation();
    if (!btn.dataset.wishlistVariant) return;

    var res = toggle(itemFrom(btn));
    paintButton(btn, res.added);
    if (res.added) {
      btn.classList.remove('is-pop');
      // reflow so the animation restarts on a repeat toggle
      void btn.offsetWidth;
      btn.classList.add('is-pop');
    }
  }, true);

  // The PDP heart must follow the variant picker: hearting a product is really
  // hearting the selected variant.
  function wirePdpVariantSync() {
    var btn = document.querySelector('[data-wishlist-toggle][data-wishlist-pdp]');
    if (!btn) return;
    var section = btn.closest('.shopify-section') || document;
    var input = section.querySelector('[data-variant-id]');
    if (!input) return;

    var variants = null;
    var json = section.querySelector('[data-variants-json]');
    if (json) { try { variants = JSON.parse(json.textContent); } catch (e) { variants = null; } }

    // product-controller.js writes [data-variant-id] inside its own
    // requestAnimationFrame, and the exact frame it lands on varies with what
    // else is on the page. Chasing that with one rAF is racy and was silently
    // leaving the heart pointing at the previous variant -- i.e. hearting the
    // wrong item. Re-run sync across a short window instead; it is idempotent
    // and cheap, so settling late costs nothing and never mis-saves.
    var syncTimers = [];
    function scheduleSync() {
      syncTimers.forEach(clearTimeout);
      syncTimers = [0, 60, 250].map(function (d) { return setTimeout(sync, d); });
    }

    function sync() {
      // Re-resolve rather than trusting closures: a section re-render (theme
      // editor, hot reload) swaps these nodes out.
      btn = document.querySelector('[data-wishlist-toggle][data-wishlist-pdp]') || btn;
      input = (btn.closest('.shopify-section') || document).querySelector('[data-variant-id]') || input;
      var id = input.value;
      if (!id) return;
      btn.dataset.wishlistVariant = id;
      if (variants) {
        for (var i = 0; i < variants.length; i++) {
          if (String(variants[i].id) !== String(id)) continue;
          var v = variants[i];
          btn.dataset.wishlistVariantTitle = v.title || '';
          btn.dataset.wishlistPrice = v.price != null ? v.price : '';
          var img = (v.featured_image && v.featured_image.src) || '';
          if (img) btn.dataset.wishlistImage = img;
          break;
        }
      }
      paintButton(btn, has(id));
    }

    // product-controller.js writes [data-variant-id] inside its OWN
    // requestAnimationFrame, so a rAF hung off the picker's change/click fires
    // FIRST and reads the previous variant. Observing the option group's
    // [data-selected-value] text instead fires after the controller has
    // written, which is the same approach the pair-offer module uses.
    var picker = section.querySelector('[data-variant-picker]');
    if (picker && 'MutationObserver' in window) {
      // Observe the PICKER itself with subtree, not the [data-selected-value]
      // node. wishlist.js is a deferred script in the layout, so it runs BEFORE
      // product-controller.js boots and re-renders the picker's innards --
      // anything captured in here at init is replaced out from under us and the
      // observer would sit on an orphaned node. The picker element survives.
      var mo = new MutationObserver(scheduleSync);
      mo.observe(picker, { childList: true, characterData: true, subtree: true });
    }
    if (picker) {
      picker.addEventListener('change', scheduleSync);
      picker.addEventListener('click', scheduleSync);
    }
    sync();
  }

  // ---- public surface ---------------------------------------------------
  // Deliberately narrow. Layer 2 swaps Store.get/Store.set for a synced
  // implementation and everything above keeps working unchanged.
  window.LEWishlist = {
    all: Store.get,
    has: has,
    add: add,
    remove: remove,
    count: function () { return Store.get().length; },
    EVENT: EVT
  };

  function init() {
    syncAllButtons();
    wirePdpVariantSync();
  }

  document.addEventListener(EVT, syncAllButtons);
  // Another tab changed the list.
  window.addEventListener('storage', function (e) {
    if (e.key === KEY) syncAllButtons();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
